/**
 * DASHBOARD LAYOUT - Unified Sidebar Navigation System
 * 
 * This component provides the consistent layout structure for all dashboard pages.
 * 
 * KEY FEATURES:
 * 1. Responsive Sidebar: Always visible on desktop, overlay on mobile
 * 2. Context Integration: Uses ChatInterfaceContext for state management
 * 3. Navigation Items: Unified access to all dashboard features
 * 4. Consistent Headers: Standardized page titles and actions
 * 5. Proper Scrolling: Handles content overflow and mobile behavior
 * 
 * SIDEBAR NAVIGATION ITEMS:
 * - Dashboard: Overview and quick stats
 * - My Posits: List and manage all created Posits
 * - Create Posit: Start new knowledge base
 * - Analytics: Performance metrics and insights
 * - Settings: Profile and account management
 * - Integrations: Third-party connections (Google Drive, etc.)
 * - Affiliate: Commission tracking (Plus tier only)
 * 
 * RESPONSIVE BEHAVIOR:
 * - Desktop: Fixed sidebar always visible (no floating menu)
 * - Mobile: Overlay sidebar that can be toggled with hamburger menu
 * - Tablet: Collapsible sidebar that respects screen real estate
 * 
 * STATE MANAGEMENT:
 * Uses ChatInterfaceContext to manage sidebar visibility across
 * all dashboard pages and maintain consistent user experience.
 * 
 * KNOWN ISSUES (Pre-Launch):
 * - Inconsistent sidebar behavior across different dashboard pages
 * - Missing unified header patterns on some pages
 * - Mobile sidebar animation needs improvement
 */

import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ChatSidebar from '@/components/chat/layout/ChatSidebar';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '7xl' | 'full';
  className?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title,
  description,
  headerActions,
  maxWidth = '7xl',
  className = ''
}) => {
  const isMobile = useIsMobile();
  const { sidebarOpen, toggleSidebar } = useChatInterface();
  const [hasMainNavigation, setHasMainNavigation] = useState(false);

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md', 
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-none'
  };

  // Check if there's a main navigation component present
  useEffect(() => {
    const mainNav = document.querySelector('header[class*="sticky"]');
    setHasMainNavigation(!!mainNav);
  }, []);

  // Listen for sidebar toggle events from main navigation
  useEffect(() => {
    const handleSidebarToggle = () => {
      try {
        toggleSidebar();
      } catch (error) {
        console.warn('Failed to toggle sidebar:', error);
      }
    };

    window.addEventListener('toggle-sidebar', handleSidebarToggle);
    return () => window.removeEventListener('toggle-sidebar', handleSidebarToggle);
  }, [toggleSidebar]);

  // Handle orientation changes to prevent layout issues
  useEffect(() => {
    const handleOrientationChange = () => {
      // Small delay to allow viewport to settle after orientation change
      setTimeout(() => {
        // Force a re-render to handle any layout issues
        setHasMainNavigation(prev => prev);
      }, 200);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  // Adjust height based on whether main navigation is present
  const containerHeight = hasMainNavigation ? 'min-h-[calc(100vh-4rem)]' : 'min-h-screen';
  const topOffset = hasMainNavigation ? 'top-16' : 'top-0';

  return (
    <div className={cn("flex bg-background relative", containerHeight)}>
      {/* Sidebar - Always visible on desktop, overlay on mobile */}
      {!isMobile && (
        <div className={cn("fixed left-0 h-screen z-30", hasMainNavigation ? "top-16" : "top-0")}>
          <ChatSidebar 
            open={true} 
            onToggle={toggleSidebar}
            variant="persistent"
          />
        </div>
      )}
      
      {/* Mobile overlay sidebar */}
      {isMobile && (
        <ChatSidebar 
          open={sidebarOpen} 
          onToggle={toggleSidebar}
          variant="overlay"
        />
      )}
      
      {/* Main Content Area - Always offset for sidebar on desktop (280px), scrollable */}
      <main className={`flex-1 min-w-0 transition-all duration-300 ${
        !isMobile ? 'ml-[280px]' : ''
      }`}>

        
        {/* Content Container - Fully scrollable */}
        <div className={`bg-background ${className}`}>
          <div className={`mx-auto ${maxWidthClasses[maxWidth]} px-5 sm:px-4 pt-8 sm:pt-8 pb-12 sm:pb-12`}>
            {/* Header */}
            {(title || description || headerActions) && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="min-w-0 flex-1">
                  {title && (
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{title}</h1>
                  )}
                </div>
                {headerActions && (
                  <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                    {headerActions}
                  </div>
                )}
              </div>
            )}
            
            {/* Main Content */}
            <div className="w-full">
              {children}
            </div>
          </div>
        </div>
      </main>
      
      {/* Mobile overlay backdrop when sidebar is open */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={toggleSidebar}
          aria-label="Close sidebar"
        />
      )}
    </div>
  );
};

export default DashboardLayout; 